import React, { useState } from 'react';
import RegionalEditPanel from './RegionalEditPanel';
import { SpinnerIcon } from './Spinner';

// Minimal CSV parser for simple 2-column CSV (no quoted commas support)
const parseCsvToRows = (text: string): string[][] => {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => line.split(','));
};

type TranslationMode = 'auto' | 'file';

interface TranslationPanelProps {
  handleAutoTranslate: (inputLang: string, outputLangs: string[]) => void;
  handleManualTranslate: (rows: TranslationRow[]) => void;
  isLoading: boolean;
  isSubmitting: boolean;
  hasImage: boolean;
  // Regional editing props
  isRegionSelectMode: boolean;
  onToggleRegionMode: () => void;
  editRegion: { x: number; y: number; width: number; height: number } | null;
  onClearRegion: () => void;
  regionAspect?: number | undefined;
  onSetRegionAspect?: (aspect: number | undefined) => void;
  onClearRegionCrop?: () => void;
}

const TranslationPanel: React.FC<TranslationPanelProps> = ({ handleAutoTranslate, handleManualTranslate, isLoading, isSubmitting, hasImage, isRegionSelectMode, onToggleRegionMode, editRegion, onClearRegion, regionAspect, onSetRegionAspect, onClearRegionCrop }) => {
  const [inputLanguage, setInputLanguage] = useState('English');
  const [outputLanguages, setOutputLanguages] = useState<string[]>(['French']);
  const [translationMode, setTranslationMode] = useState<TranslationMode>('auto');
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<TranslationRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleTranslate = () => {
    if (translationMode === 'auto') {
      handleAutoTranslate(inputLanguage, outputLanguages);
    } else {
      handleManualTranslate(rows);
    }
  }

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'French' },
    { code: 'es', name: 'Spanish' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ru', name: 'Russian' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ar', name: 'Arabic' },
    { code: 'hi', name: 'Hindi' },
  ];

  const toggleOutputLanguage = (langName: string) => {
    setOutputLanguages(prev => 
      prev.includes(langName) 
        ? prev.filter(l => l !== langName)
        : [...prev, langName]
    );
  };

  const editInputLanguage = (langName: string) => {
    if (outputLanguages.includes(langName)) {
      setOutputLanguages(prev => prev.filter(l => l !== langName));
    }
    setInputLanguage(langName);
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFileName(null);
      setRows([]);
      setParseError(null);
      return;
    }
    setSelectedFileName(file.name);
    setParseError(null);

    file.text().then((content) => {
      try {
        const data = parseCsvToRows(content);

        if (!Array.isArray(data) || data.length === 0) {
          setRows([]);
          setParseError('The file is empty or could not be parsed.');
          return;
        }

        const [firstRow, ...rest] = data;
        const looksLikeHeader =
          firstRow &&
          firstRow.length >= 2 &&
          /original/i.test(firstRow[0] ?? '') &&
          /target|translation/i.test(firstRow[1] ?? '');

        const rowsData = (looksLikeHeader ? rest : data).map((r, index) => ({
          id: index,
          original: String(r[0] ?? ''),
          target: String(r[1] ?? ''),
          group_id: String(r[2] ?? '1'),
        }));

        setRows(rowsData);
      } catch (e) {
        setParseError('Failed to interpret the file content as CSV.');
      }
    }).catch(() => {
      setParseError('Failed to read the file.');
    });
  };

  const handleCellChange = (id: number, field: keyof Omit<TranslationRow, 'id'>, value: string) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const handleAddRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: prev.length ? prev[prev.length - 1].id + 1 : 0,
        original: '',
        target: '',
        group_id: '1',
      },
    ]);
  };

  const handleRemoveRow = (id: number) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const uniqueVersions = new Set(rows.map((r) => r.group_id || '1')).size;

  return (
    <div className="space-y-4">
      {/* Regional Editing Toggle */}
      <RegionalEditPanel
        isRegionSelectMode={isRegionSelectMode}
        onToggleRegionMode={onToggleRegionMode}
        editRegion={editRegion}
        onClearRegion={onClearRegion}
        isLoading={isLoading}
        regionAspect={regionAspect}
        onSetRegionAspect={onSetRegionAspect}
        onClearRegionCrop={onClearRegionCrop}
        description="Select a specific region of the image to translate instead of the entire image."
        activeDescription="Draw a zone on the image to select the region containing text to translate."
      />

      {/* Mode selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-gray-300 font-medium">Translation mode</div>
        <div className="inline-flex rounded-lg bg-slate-900 p-1 border border-slate-700">
          <button
            type="button"
            onClick={() => setTranslationMode('auto')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              translationMode === 'auto'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:bg-slate-800'
            }`}
          >
            Automatic
          </button>
          <button
            type="button"
            onClick={() => setTranslationMode('file')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              translationMode === 'file'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:bg-slate-800'
            }`}
          >
            File mapping (CSV)
          </button>
        </div>
      </div>

      {/* Languages (only for automatic mode) */}
      {translationMode === 'auto' && (
        <div className="space-y-4">
          {/* Input Language */}
          <div className="space-y-2">
            <label htmlFor="input-language" className="block text-sm font-medium text-gray-300">
              Input Language
            </label>
            <select
              id="input-language"
              value={inputLanguage}
              onChange={(e) => editInputLanguage(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.name} className="bg-slate-700">
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          {/* Output Languages (Multi-select) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-300">
                Output Languages
              </label>
              {outputLanguages.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-900/50 text-blue-200 border border-blue-800">
                  {outputLanguages.length} language{outputLanguages.length !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 bg-slate-700/50 border border-slate-600 rounded-lg max-h-48 overflow-y-auto">
              {languages.filter(lang => lang.name !== inputLanguage).map((lang) => (
                <label
                  key={lang.code}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                    outputLanguages.includes(lang.name)
                      ? 'bg-blue-600/80 text-white border border-blue-500'
                      : 'bg-slate-800 text-gray-300 border border-slate-700 hover:bg-slate-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={outputLanguages.includes(lang.name)}
                    onChange={() => toggleOutputLanguage(lang.name)}
                    className="w-4 h-4 rounded border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 bg-slate-700"
                  />
                  <span className="text-xs font-medium">{lang.name}</span>
                </label>
              ))}
            </div>
            {outputLanguages.length === 0 && (
              <p className="text-xs text-red-400 mt-1">Please select at least one output language</p>
            )}
          </div>

          {/* Translate Button (auto mode) */}
          <div className="flex justify-end pt-2">
            <button
              onClick={handleTranslate}
              className="bg-gradient-to-br from-blue-600 to-blue-500 text-white font-bold py-2 px-8 text-lg rounded-lg transition-all duration-300 ease-in-out shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/40 hover:-translate-y-px active:scale-95 active:shadow-inner disabled:from-blue-800 disabled:to-blue-700 disabled:shadow-none disabled:cursor-not-allowed disabled:transform-none"
              disabled={isLoading || isSubmitting || !hasImage || outputLanguages.length === 0}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <SpinnerIcon />
                  Submitting...
                </span>
              ) : (
                `Translate to ${outputLanguages.length} language${outputLanguages.length !== 1 ? 's' : ''}`
              )}
            </button>
          </div>
        </div>
      )}

      {/* File upload + editable table for custom mapping */}
      {translationMode === 'file' && (
        <div className="mt-2 space-y-3">
          <div className="border border-dashed border-slate-600 rounded-lg p-4 bg-slate-900/40">
            <p className="text-sm text-gray-300 mb-2 font-medium">Upload your mapping file</p>
            <p className="text-xs text-gray-400 mb-3">
              CSV file with columns: <span className="font-mono">original_text</span>, <span className="font-mono">target_text</span>, and optionally <span className="font-mono">version_id</span>.
              <br />
              Use different <span className="font-mono">version_id</span>s to generate multiple images (e.g. "EN", "FR").
            </p>
            <label className="flex items-center gap-3 cursor-pointer">
              <span className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-md bg-slate-700 text-gray-100 border border-slate-500 hover:bg-slate-600">
                Choose file
              </span>
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileChange}
              />
              <span className="text-xs text-gray-400 truncate max-w-[200px]">
                {selectedFileName ?? 'No file selected'}
              </span>
            </label>
            {parseError && (
              <p className="mt-2 text-xs text-red-400">{parseError}</p>
            )}
          </div>

          {/* Editable table */}
          <div className="border border-slate-700 rounded-lg overflow-hidden bg-slate-900/60">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700 bg-slate-900/80">
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-300 font-medium">Custom translations</p>
                {rows.length > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-900/50 text-blue-200 border border-blue-800">
                    {uniqueVersions} version{uniqueVersions !== 1 ? 's' : ''} detected
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={handleAddRow}
                className="text-xs px-2 py-1 rounded bg-slate-700 text-gray-100 border border-slate-500 hover:bg-slate-600"
              >
                + Add row
              </button>
            </div>
            <div className="max-h-72 overflow-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-300 w-[40%]">Original text</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-300 w-[40%]">Target text</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-300 w-[15%]">Version ID</th>
                    <th className="px-2 py-2 w-[5%]" />
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-3 py-4 text-center text-gray-500 text-xs"
                      >
                        No rows yet. Upload a CSV or add rows manually.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id} className="border-t border-slate-800">
                        <td className="px-3 py-1.5 align-top">
                          <textarea
                            value={row.original}
                            onChange={(e) => handleCellChange(row.id, 'original', e.target.value)}
                            rows={2}
                            className="w-full resize-y bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-xs text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-3 py-1.5 align-top">
                          <textarea
                            value={row.target}
                            onChange={(e) => handleCellChange(row.id, 'target', e.target.value)}
                            rows={2}
                            className="w-full resize-y bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-xs text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-3 py-1.5 align-top">
                          <input
                            type="text"
                            value={row.group_id || ''}
                            onChange={(e) => handleCellChange(row.id, 'group_id', e.target.value)}
                            className="w-full bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-xs text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-2 py-1.5 align-top text-right">
                          <button
                            type="button"
                            onClick={() => handleRemoveRow(row.id)}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-gray-300 border border-slate-600 hover:bg-red-700 hover:text-white"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Translate Button for file mode */}
          <div className="pt-2 flex justify-end">
            <button
              onClick={handleTranslate}
              className="bg-gradient-to-br from-blue-600 to-blue-500 text-white font-bold py-2 px-8 text-lg rounded-lg transition-all duration-300 ease-in-out shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/40 hover:-translate-y-px active:scale-95 active:shadow-inner disabled:from-blue-800 disabled:to-blue-700 disabled:shadow-none disabled:cursor-not-allowed disabled:transform-none"
              disabled={isLoading || isSubmitting || !hasImage || rows.length === 0}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <SpinnerIcon />
                  Submitting...
                </span>
              ) : 'Translate'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranslationPanel;
